import { CloseOutlined, DownOutlined, PlusCircleOutlined, SwapRightOutlined } from '@ant-design/icons';
import { useControllableValue } from 'ahooks';
import {
  Input,
  Dropdown,
  Space,
  Select,
  Button,
  InputNumber,
  Form,
  Tooltip,
  FormInstance,
  AutoComplete,
} from 'antd';
import classNames from 'classnames';
import { get, remove, isArray, isEmpty } from 'lodash-es';

import React, { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { DATA_TID } from '@/infrastructure/cypress-ids';
import { getProductId } from '@/infrastructure/product-info';
import { FormEntry } from '@/presentation/components/form';
import { getBuglyCustomKeys, getCustomUserKeys, getExtendInfoKeys } from '@/presentation/components/form/api';
import {
  FieldsOperator,
  fieldsOperators,
  FieldValueType,
} from '@/presentation/components/form/proto';
import { MATCH_MODE_TEXT, MatchMode } from '@/presentation/components/form/standards/types';
import { IssueType } from '@/proto/issue';
import { KeyDataType } from '@/proto/token';
import { IUserKey } from '@/proto/user';
import { toLineString } from '@raft/utils/src/case/snake-case';
import { BaseFormItemProps } from '../type';
import styles from './index.module.scss';

export interface IProps {
  defaultValue?: any;
  value?: any;
  entry: FormEntry;
  issueType?: IssueType;
  type: CustomType;
}

export interface TdsCustomFieldsItemProps {
  index: number;
  columns?: IUserKey[];
  onDelete?: (index: number) => void;
  form: FormInstance;
  onChange?: (values: CustomFieldValue, index: number) => void;
  type: CustomType;
  loading?: boolean;
  name: string;
}

export interface TdsCustomFieldsPanelProps extends BaseFormItemProps<any> {
  // name: string;
  issueType: IssueType;
  type: CustomType;
  className?: string
  style?: CSSProperties;
}

export type CustomType = 'customKey' | 'customFields' | 'buglyCustomKey';

export type CustomKey = {
  label: string;
  key: string;
  type: number;
};

export type CustomFieldValue = {
  operator?: string;
  key?: string;
  values?: string[] | string;
  type?: KeyDataType;
  name?: string;
};

// cypress test id
const { addBtn, confirmBtn, cancelBtn, itemDelete, itemKey, itemOP, itemValue } = DATA_TID.customField;

const defaultFilter = {
  name: undefined,
  operator: undefined,
  values: undefined,
};

type RangeValue =  [number | undefined, number | undefined];

const InputNumberRange = (props: {
  onChange?: (rangeValue: RangeValue) => void;
  value?: RangeValue;
  defaultValue?: RangeValue;
  className?: string;
}) => {
  const { defaultValue = [undefined, undefined] } = props;
  const [rangeValue, setRangeValue] = useState<RangeValue>(props.value || defaultValue);

  const onChage = (value: number, index: number) => {
    const range = [...rangeValue];
    range[index] = value;
    setRangeValue(range as RangeValue);
    props.onChange?.(range as RangeValue);
  };

  useEffect(() => {
    setRangeValue(rangeValue);
  }, [props.value]);

  return (
    <div className={classNames(props.className, styles.inputRange)}>
      <InputNumber
        value={rangeValue[0]}
        onChange={value => onChage(value as number, 0)}
        data-tid={itemValue.numberFrom}
      />
      <SwapRightOutlined />
      <InputNumber
        value={rangeValue[1]}
        onChange={value => onChage(value as number, 1)}
        data-tid={itemValue.numberTo}
      />
    </div>
  );
};

const CustomFieldsItem: React.FC<TdsCustomFieldsItemProps> = (props) => {
  const { columns = [], form, index, type } = props;
  const [selectedKey, setSelectedKey] = useState<CustomKey | null>(null);
  const [operator, setOperator] = useState<FieldsOperator | null>(null);

  const KeyComponent = type === 'buglyCustomKey' ? AutoComplete : Select;

  const onDelete = () => {
    props.onDelete?.(props.index);
  };

  const customPrefix = `${props.name}`;

  const operatorOptions: any = useMemo(
    () => {
      if (type === 'buglyCustomKey') {
        return [
          {
            label: '是',
            value: FieldsOperator.Is,
            ['data-tid']: `${customPrefix}-operator-是-${index}`,
          },
          {
            label: '不是',
            value: FieldsOperator.ISNOT,
            ['data-tid']: `${customPrefix}-operator-不是-${index}`,
          },
          {
            label: '匹配',
            value: FieldsOperator.CONTAIN,
            ['data-tid']: `${customPrefix}-operator-匹配-${index}`,
          },
          {
            label: '不匹配',
            value: FieldsOperator.NOTCONTAIN,
            ['data-tid']: `${customPrefix}-operator-不匹配-${index}`,
          },
        ];
      }
      const options = fieldsOperators[selectedKey?.type as FieldValueType] ?? [];
      return options.map(item => ({
        ...item,
        ['data-tid']: `${customPrefix}-operator-${item.label}-${index}`,
      }));
    },
    [selectedKey],
  );

  const customKeysOptions = useMemo(() => columns.map(item => ({
    label: item.label,
    value: item.key,
    ['data-tid']: `${customPrefix}-name-${item.label}-${index}`,
  })), [columns]);

  const onKeyChange = (key: string) => {
    const currentKey = columns?.find(item => item.key === key);
    setSelectedKey(currentKey as CustomKey);
    form.setFieldsValue({
      key: currentKey?.key || key,
      type: currentKey?.type || KeyDataType.STRING,
    });
    if (currentKey?.type !== undefined && type !== 'buglyCustomKey') {
      form.setFieldsValue({
        operator: get(fieldsOperators, currentKey?.type as KeyDataType)?.[0]?.value,
      });
    }
  };

  const onOperatorChange = (operator: string) => {
    const op = operator === '' ? FieldsOperator.Eq : (operator as FieldsOperator);
    setOperator(op);
    form.setFieldsValue({
      values: undefined,
      operator: op,
    });
  };

  const renderFieldValueItem = (type: KeyDataType) => {
    if (type === KeyDataType.NUMBER) {
      return operator === FieldsOperator.Between ? (
        <InputNumberRange className={styles.rangeValues} />
      )
        : (
        <InputNumber
          className={styles.values}
          placeholder={'Value'}
          data-tid={`${itemValue.number}-${index}`}
        />
        );
    }
    if (type === KeyDataType.ARRAY) {
      return <Select
        className={styles.values}
        mode="tags"
        open={false}
        placeholder={'Value'}
        maxTagCount='responsive'
        showSearch
        data-tid={`${itemValue.array}-${index}`}
      />;
    }

    return <Select
      mode={'tags'}
      className={styles.values}
      placeholder={'Value'}
      data-tid={`${itemValue.default}-${index}`}
      disabled={[MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(operator as any)}
    />;
  };

  useEffect(() => {
    const value = form.getFieldsValue();
    value && onKeyChange(value.key);
    value.operator !== undefined && onOperatorChange(value.operator);
    // 重新设置一次值，让更新的选项可以选上
    form.setFieldsValue({
      ...value,
      operator: value.operator === '' ? FieldsOperator.Eq : (value.operator as FieldsOperator),
    });
  }, []);

  return (
    <Form
      preserve
      form={form}
      className={styles.customForm}
      onFieldsChange={() => {
        props.onChange?.(form.getFieldsValue(), index);
      }}>
      <Form.Item hidden name='type' />
      <Space size={8}>
        <Form.Item
          name='key'
        >
          <KeyComponent
            filterOption={(inputValue, option) => option!.label?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
            className={styles.column}
            options={customKeysOptions}
            placeholder='Key或别名'
            onChange={onKeyChange}
            showSearch
            dropdownMatchSelectWidth={250}
            data-tid={`${itemKey}-${index}`}
          />
        </Form.Item>
        <Form.Item
          name='operator'
          initialValue={operatorOptions?.[0]?.value}
        >
          <Select
            options={operatorOptions}
            className={styles.operator}
            placeholder='请选择'
            onChange={onOperatorChange}
            data-tid={`${itemOP}-${index}`}
          />
        </Form.Item>
        <Form.Item
          name='values'
        >
          {renderFieldValueItem(selectedKey?.type as KeyDataType)}
        </Form.Item>
        <CloseOutlined data-tid={`${itemDelete}-${index}`} className={styles.close} onClick={onDelete}/>
      </Space>
    </Form>
  );
};

export const TdsCustomFieldsPanel: React.FC<TdsCustomFieldsPanelProps> = (props) => {
  const { issueType, type, className } = props;
  const [value, setValue] = useControllableValue<CustomFieldValue[]>(props, {
    defaultValue: [],
  });
  const [visible, setVisible] = useState(false);
  const [customKeys, setCustomKeys] = useState<IUserKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // 临时存储，点击确认的时候才触发form onchange
  const [customFilters, setCustomFilters] = useState<CustomFieldValue[]>(isEmpty(value) ? [defaultFilter] : value);
  const forms = useRef<FormInstance[]>([]);

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const FieldItemContainer = (props: {
    index: number,
    onDelete?: (index: number) => void
  }) => {
    const { index, onDelete } = props;
    const [form] = Form.useForm();

    if (!forms.current[index]) {
      forms.current[index] = form;
      forms.current[index].setFieldsValue(customFilters[index]);
    }

    return (
      <CustomFieldsItem
        form={forms.current[index]}
        index={index}
        columns={customKeys}
        onDelete={onDelete}
        type={type}
        loading={loading}
        name={'custom'}
      />
    );
  };

  const onDelete = (index: number) => {
    const tempData = [...customFilters];
    const tempForms = [...forms.current];
    remove(tempData, (_: CustomFieldValue, i: number) => index === i);
    remove(tempForms, (_: FormInstance, i: number) => index === i);
    forms.current = tempForms;
    setCustomFilters(tempData);
  };

  const renderFilters = () => customFilters.map((_filter, index) => (
    <FieldItemContainer index={index} onDelete={onDelete} />
  ));

  const addFilter = () => {
    setCustomFilters([
      ...customFilters,
      defaultFilter,
    ]);
  };

  const getFilterValue = (item: CustomFieldValue) => {
    if ([MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item?.operator as any)) {
      return [];
    }
    const stringValue = `${item?.values as string}`;
    if (!isArray(item.values)) return stringValue?.split(';');
    if (item?.operator === FieldsOperator.Between) return stringValue?.split(';');

    return item?.values;
  };

  const filterEmptyValues = (fieldValues: CustomFieldValue[]) => {
    const values: CustomFieldValue[] = [];
    fieldValues.forEach((item) => {
      const isValidFilterItem = (item.key && item.operator && item.values)
        || [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any);


      if (isValidFilterItem) {
        values.push({
          ...item,
          values: getFilterValue(item),
          name: toLineString('custom'),
          operator: item.operator === FieldsOperator.Eq ? '' : item.operator,
        });
      }
    });

    return values;
  };

  const getFilterText = (values: CustomFieldValue[]) => values.map((item) => {
    const operatorText = MATCH_MODE_TEXT[(item.operator === '' ? MatchMode.Eq : item.operator) as string];
    const currentKey = customKeys.find(customKey => customKey.key === item.key);
    const values = [MatchMode.ISEMPTY, MatchMode.NOTEMPTY].includes(item.operator as any) ? '' : JSON.stringify(item.values);
    return `${currentKey?.label || item?.key} 的值 ${operatorText} ${values}`;
  }).join(' 且 ');

  const onConfirm = async () => {
    try {
      const formItemValues = forms.current.map(form => form.getFieldsValue());
      const values = filterEmptyValues(formItemValues);
      hideOverlay();
      setValue(values);
    } catch (e) {
    }
  };

  const overlay = (
    <div className={styles.customOverlay}>
      <span>以下的关系为全且</span>
      <div className={styles.itemContainer}>
        {renderFilters()}
      </div>
      <div>
        <Space className={styles.addButton} size={3}>
          <PlusCircleOutlined/>
          <span data-tid={addBtn} onClick={addFilter}>添加筛选</span>
        </Space>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button data-tid={cancelBtn} onClick={hideOverlay}>取消</Button>
          <Button data-tid={confirmBtn} type={'primary'} onClick={onConfirm}>确定</Button>
        </Space>
      </div>
    </div>
  );

  const filterText = useMemo(() => {
    if (!value || isEmpty(value)) return '';
    return getFilterText(value as CustomFieldValue[]);
  }, [value]);

  useEffect(() => {
    const load = async () => {
      const service = props.type === 'customKey' ? getExtendInfoKeys : getCustomUserKeys;
      setLoading(true);
      if (type === 'buglyCustomKey') {
        const keys = await getBuglyCustomKeys();
        setCustomKeys(keys.map((key: string) => ({
          label: key,
          key,
          type: 1,
        })));
      } else {
        const keys = await service({
          productId: getProductId(),
          issueType,
        });

        setCustomKeys(keys);
      }

      setLoading(false);
      setLoaded(true);
    };

    (visible && !loaded) && load();
  }, [issueType, visible]);

  useEffect(() => {
    setCustomFilters(!isEmpty(value) ? value : [defaultFilter]);
  }, [value]);

  return (
    <div className={className ? className : styles.panel} style={props.style}>
      <Space size={0} className={styles.panelSpace} direction={'vertical'}>
        <Tooltip title={filterText}>
          <Input
            placeholder='多个值通过按回车分隔'
            onClick={showOverlay}
            suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
            style={{ width: '100%' }}
            value={filterText}
          />
        </Tooltip>
        <Dropdown
          overlay={overlay}
          trigger={['click']}
          visible={visible}
          destroyPopupOnHide={false}
          onVisibleChange={visible => setVisible(visible)}
        >
          <div style={{ height: 1 }}/>
        </Dropdown>
      </Space>
    </div>
  );
};
